import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRightIcon,
  LockClosedIcon,
  ChartBarIcon,
  KeyIcon,
  HeartIcon,
} from "@heroicons/react/24/solid";
import whyChooseTablet from "@/assets/why-choose-tablet-v2.webp";

interface FeatureCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
  linkText: string;
  index: number;
}

function FeatureCard({ icon: Icon, title, description, href, linkLabel, linkText, index }: FeatureCardProps) {
  return (
    <motion.div
      className="flex flex-col gap-4 p-6 -m-6 rounded-xl items-start"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
    >
      <Icon className="w-10 h-10 text-beacon" aria-hidden="true" />
      <h3 className="text-h4 text-anchor">{title}</h3>
      <p className="text-body text-anchor">{description}</p>
      <Link
        to={href}
        aria-label={linkLabel}
        className="mt-2 inline-flex items-center gap-1.5 text-body text-beacon hover:text-beacon-deep hover:underline underline-offset-4 transition-colors"
      >
        {linkText}
        <ArrowUpRightIcon className="w-4 h-4" aria-hidden="true" />
      </Link>
    </motion.div>
  );
}


const features = [
  {
    icon: LockClosedIcon,
    title: "Simple, Secure, Reliable Connection for Hard-to-Reach Populations.",
    description:
      "Consumer tech wasn't built for complex senior populations. GrandPad is. Our senior-first design removes frustration, confusion, and risk—so even the most tech-hesitant older adults can confidently connect with family and care teams.",
    href: "/simple-secure-engagement",
    linkLabel: "Learn more about simple, secure engagement",
    linkText: "Explore engagement solutions",
  },
  {
    icon: ChartBarIcon,
    title: "Designed for Older Adults. Trusted by Care Teams. Backed by Results.",
    description:
      "GrandPad reduces social isolation and makes it easier for older adults to participate in care, leading to measurable improvements in mood, engagement, and adherence.",
    href: "/evidence-based-outcomes",
    linkLabel: "Learn more about evidence-based outcomes",
    linkText: "Explore proven results",
  },
  {
    icon: KeyIcon,
    title: "One Turnkey Solution—From Deployment to Daily Use.",
    description:
      "GrandPad delivers hardware, software, connectivity, logistics, and support in one fully connected ecosystem—reducing complexity and giving providers a frictionless way to engage older adults.",
    href: "/turnkey-enablement",
    linkLabel: "Learn more about turnkey enablement",
    linkText: "Explore turnkey enablement",
  },
  {
    icon: HeartIcon,
    title: "Real People. Real Support. Real Empowerment.",
    description:
      "Support that scales without losing the human touch. Our world-class, U.S.-based customer care team makes technology feel welcoming and safe for older adults—while easing friction and reducing workload for families and care teams.",
    href: "/person-centered-support",
    linkLabel: "Learn more about person-centered support",
    linkText: "Explore support options",
  },
];

export function WhyChooseSection() {
  return (
    <section className="w-full bg-sailcloth pt-24 pb-12 max-lg:pt-16 max-lg:pb-8" aria-labelledby="why-choose-heading">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32">
        {/* Header - Centered */}
        <motion.div
          className="text-center mb-16 max-md:mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-caption uppercase tracking-[0.2em] text-beacon mb-4">The GrandPad Difference</p>
          <h2 id="why-choose-heading" className="text-h1 text-foreground mb-6">
            Why Healthcare Organizations Choose GrandPad
          </h2>
          <p className="text-body-lg max-w-3xl mx-auto text-anchor">
            GrandPad combines senior-first design, turnkey enablement, proven engagement, and person-centered support
            into one integrated solution.
          </p>
        </motion.div>

        {/* 2x2 Feature Grid */}
        <div className="grid grid-cols-2 gap-x-16 gap-y-16 max-md:grid-cols-1 max-md:gap-12">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              href={feature.href}
              linkLabel={feature.linkLabel}
              linkText={feature.linkText}
              index={index}
            />
          ))}
        </div>

        {/* Tablet Image */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
        >
          <img
            src={whyChooseTablet}
            alt="GrandPad tablet showing the simple, senior-friendly interface"
            className="max-w-[70%] max-lg:max-w-full h-auto"
            width={1800}
            height={900}
            loading="lazy"
            decoding="async"
          />
        </motion.div>
      </div>
    </section>
  );
}
